import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { UserCog, X, Plus, ShieldCheck, Mail } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = async (opts = {}) => {
  const res = await fetch(`${API_URL}/api/ean-owners`, { credentials: 'include', headers: { 'Content-Type': 'application/json' }, ...opts });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.detail || 'Request failed');
  return body;
};

export const EanOwnersTab = () => {
  const [data, setData] = useState(null);
  const [emails, setEmails] = useState([]);
  const [input, setInput] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { api().then((d) => { setData(d); setEmails(d.emails); }).catch((e) => toast.error(e.message)); }, []);

  const add = (e) => {
    e.preventDefault();
    const v = input.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) { toast.error('Enter a valid email'); return; }
    if (!emails.includes(v)) setEmails([...emails, v]);
    setInput('');
  };
  const save = async () => {
    setSaving(true);
    try { const d = await api({ method: 'PUT', body: JSON.stringify({ emails }) }); setData(d); setEmails(d.emails); toast.success('EAN owners saved'); }
    catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };
  const dirty = data && JSON.stringify(emails) !== JSON.stringify(data.emails);
  const canEdit = data?.is_ean_admin;

  return (
    <Card className="bg-white border border-slate-200 shadow-sm" data-testid="ean-owners-card">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-[#2EB4AC]/10 flex items-center justify-center"><UserCog className="h-6 w-6 text-[#2EB4AC]" /></div>
          <div>
            <CardTitle className="text-lg">EAN Module Owners</CardTitle>
            <CardDescription>Owners get an email for every new EAN request and can mark requests In Progress / Generated, set EAN, SKU and barcode.</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col gap-2">
          {emails.length === 0 && <p className="text-sm text-slate-400">No owners added yet</p>}
          {emails.map((e) => (
            <div key={e} className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2" data-testid={`ean-owner-${e}`}>
              <Mail className="h-4 w-4 text-slate-400" />
              <span className="flex-1 text-sm text-slate-800">{e}</span>
              {canEdit && (
                <button type="button" onClick={() => setEmails(emails.filter((x) => x !== e))} className="text-slate-400 hover:text-red-500" data-testid={`ean-owner-remove-${e}`}>
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
          <div className="flex items-center gap-3 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-500" data-testid="ean-owner-fallback">
            <ShieldCheck className="h-4 w-4 text-[#2EB4AC]" />
            <span className="flex-1">{data?.fallback}</span>
            <span className="text-xs">Permanent admin</span>
          </div>
        </div>
        {canEdit ? (
          <div className="flex flex-wrap gap-2">
            <form onSubmit={add} className="flex flex-1 gap-2 min-w-[280px]">
              <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="owner@wellversed.in" data-testid="ean-owner-input" />
              <Button type="submit" variant="outline" data-testid="ean-owner-add-btn"><Plus className="h-4 w-4 mr-1" /> Add</Button>
            </form>
            <Button onClick={save} disabled={!dirty || saving} className="bg-[#2EB4AC] hover:bg-[#25908A]" data-testid="ean-owner-save-btn">Save owners</Button>
          </div>
        ) : data && <p className="text-xs text-slate-400">Only EAN owners can change this list.</p>}
      </CardContent>
    </Card>
  );
};
